import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { toast } from "sonner";
import { clockingApi } from "@/network/network.jsx";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Label } from "@/modern/components/ui/label";
import { Switch } from "@/modern/components/ui/switch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/modern/components/ui/sheet";
import type { ClockingSettings } from "./types";

type ClockingSettingsSheetProps = {
  boardId: number | string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export function ClockingSettingsSheet({
  boardId,
  open,
  onOpenChange,
  onSaved,
}: ClockingSettingsSheetProps) {
  const [settings, setSettings] = useState<ClockingSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    void clockingApi(boardId)
      .getSettings()
      .then(({ data }) => {
        if (!cancelled) setSettings(data as ClockingSettings);
      })
      .catch(() => {
        if (!cancelled) toast.error("Unable to load clocking settings.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [boardId, open]);

  const save = async () => {
    if (!settings) return;
    setIsSaving(true);
    try {
      await clockingApi(boardId).patchSettings({
        clocking_available: settings.clocking_available,
        default_geofence_radius_m: settings.default_geofence_radius_m,
        default_daily_scheduled_start: settings.default_daily_scheduled_start,
        default_daily_scheduled_end: settings.default_daily_scheduled_end,
        incentives_enabled: settings.incentives_enabled,
        clock_in_points: settings.clock_in_points,
        clock_out_points: settings.clock_out_points,
        missed_clock_penalty: settings.missed_clock_penalty,
      });
      toast.success("Clocking settings saved");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Unable to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Clocking settings</SheetTitle>
          <SheetDescription>Board defaults for geofence, schedule, and rewards.</SheetDescription>
        </SheetHeader>

        {isLoading || !settings ? (
          <p className="mt-6 text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="mt-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <Label htmlFor="clocking-available" className="text-sm font-medium">
                  Clocking available
                </Label>
                <p className="text-xs text-muted-foreground">
                  Turn Clocking on for this board so workers can punch in/out and HQ can review
                  attendance.
                </p>
              </div>
              <Switch
                id="clocking-available"
                className="mt-0.5"
                checked={settings.clocking_available}
                onCheckedChange={(v) =>
                  setSettings((s) => (s ? { ...s, clocking_available: v } : s))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="geofence" className="text-sm font-medium">
                Default geofence (m)
              </Label>
              <Input
                id="geofence"
                type="number"
                value={settings.default_geofence_radius_m}
                onChange={(e) =>
                  setSettings((s) =>
                    s
                      ? { ...s, default_geofence_radius_m: Number(e.target.value) || 0 }
                      : s
                  )
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sched-start" className="text-sm font-medium">
                  Default start
                </Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="sched-start"
                    type="time"
                    autoComplete="off"
                    value={settings.default_daily_scheduled_start?.slice(0, 5) || "08:00"}
                    onChange={(e) =>
                      setSettings((s) =>
                        s
                          ? { ...s, default_daily_scheduled_start: `${e.target.value}:00` }
                          : s
                      )
                    }
                    className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sched-end" className="text-sm font-medium">
                  Default end
                </Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="sched-end"
                    type="time"
                    autoComplete="off"
                    value={settings.default_daily_scheduled_end?.slice(0, 5) || "17:00"}
                    onChange={(e) =>
                      setSettings((s) =>
                        s ? { ...s, default_daily_scheduled_end: `${e.target.value}:00` } : s
                      )
                    }
                    className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <Label htmlFor="incentives-enabled" className="text-sm font-medium">
                  Incentives enabled
                </Label>
                <p className="text-xs text-muted-foreground">
                  Award points for clean punches and apply a penalty for missed clocks.
                </p>
              </div>
              <Switch
                id="incentives-enabled"
                className="mt-0.5"
                checked={settings.incentives_enabled}
                onCheckedChange={(v) =>
                  setSettings((s) => (s ? { ...s, incentives_enabled: v } : s))
                }
              />
            </div>

            {settings.incentives_enabled && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clock-in-pts" className="text-sm font-medium">
                    Clock-in pts
                  </Label>
                  <Input
                    id="clock-in-pts"
                    type="number"
                    value={settings.clock_in_points}
                    onChange={(e) =>
                      setSettings((s) =>
                        s ? { ...s, clock_in_points: Number(e.target.value) || 0 } : s
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clock-out-pts" className="text-sm font-medium">
                    Clock-out pts
                  </Label>
                  <Input
                    id="clock-out-pts"
                    type="number"
                    value={settings.clock_out_points}
                    onChange={(e) =>
                      setSettings((s) =>
                        s ? { ...s, clock_out_points: Number(e.target.value) || 0 } : s
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="missed-penalty" className="text-sm font-medium">
                    Missed penalty
                  </Label>
                  <Input
                    id="missed-penalty"
                    type="number"
                    value={settings.missed_clock_penalty}
                    onChange={(e) =>
                      setSettings((s) =>
                        s ? { ...s, missed_clock_penalty: Number(e.target.value) || 0 } : s
                      )
                    }
                  />
                </div>
              </div>
            )}

            <Button className="w-full" disabled={isSaving} onClick={() => void save()}>
              {isSaving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
